import {createModel} from '@rematch/core';
import produce, {Draft} from 'immer';
import {httpGetJson} from '../../../api/http';
import {api} from '../../../../config';
import {
  AddToCalendarLabels,
  CmsData,
  CmsPageWallet,
  CmsResponse,
  ErrorMessages,
  GtmConfig,
  MapLabels,
  NoOrdersLabels,
  NoUpcomingOrdersLabels,
  PreviousOrderLabels,
  ServicePopupLabels,
  State,
  UpcomingOrderLabels,
  WalletFormCms,
} from './types';
import {RequestStatus} from '../../../types/RequestStatus';
import {AppRootState} from '../../';
import {RootModel} from '..';
import {getHeaderLanguageCode, getLanguageCode} from '../../../utils/language';

const initialState: State = {
  status: RequestStatus.Pending,
  data: {},
};

export const cms = createModel<RootModel>()({
  state: initialState,
  effects(dispatch) {
    return {
      async request(
        {cmsPaths}: {cmsPaths: string[]},
        rootState
      ): Promise<void> {
        const requestStatus = selectCmsStatus(rootState);
        if (requestStatus === RequestStatus.Fulfilled) {
          return;
        }
        try {
          const headers = getHeaderLanguageCode(
            getLanguageCode(window.location.pathname)
          );
          const pathsQuery = cmsPaths.map((path) => `public.${path}`).join(',');

          const cmsData = await httpGetJson<CmsResponse>({
            path: `${api.rainbowApiClient}/cms?cmsPaths=${pathsQuery}`,
            options: {headers},
          });

          dispatch.cms.updateData(cmsData.public);
          dispatch.cms.updateRequestStatus(RequestStatus.Fulfilled);
        } catch (error) {
          dispatch.cms.updateRequestStatus(RequestStatus.Rejected);
          throw error;
        }
      },
    };
  },
  reducers: {
    updateData: produce((draft: Draft<State>, data: CmsData) => {
      Object.assign(draft.data, data);
    }),

    updateRequestStatus: produce(
      (draft: Draft<State>, status: RequestStatus) => {
        draft.status = status;
      }
    ),
  },
});

const selectCmsRootState = (state: AppRootState): State => state.cms;
export const selectCmsStatus = (state: AppRootState): RequestStatus => {
  return selectCmsRootState(state).status;
};

export const selectCmsData = (state: AppRootState): CmsData => {
  return selectCmsRootState(state).data as CmsData;
};

export const selectCmsMapLabels = (state: AppRootState): MapLabels => {
  const data = selectCmsData(state);
  const {
    venue,
    page: {maps, account},
  } = data;
  return {
    reviewCountTemplate: venue['reviews-template'],
    directions: account['map-popup']['get-directions'],
    apiKey: maps['google-map']['api-key'],
    url: maps['google-map']['url'],
  };
};

export const selectCmsPreviousOrderLabels = (
  state: AppRootState
): PreviousOrderLabels => {
  const data = selectCmsData(state);
  const {booking} = data.page.account;
  return {
    rebook: booking['rebook'],
    more: booking['more'],
    leaveReview: booking['leave-review'],
    noShow: booking['no-show'],
    cancelled: booking['cancelled'],
    spaBreakNights: booking['spa-break-nights'],
    spaDay: booking['spa-day'],
    orderReference: booking['order-reference'],
    alternativeVenue: booking['alternative-venue'],
    venueUnavailable: booking['venue-unavailable'],
  };
};

export const selectCmsUpcomingOrderLabels = (
  state: AppRootState
): UpcomingOrderLabels => {
  const data = selectCmsData(state);
  const {booking} = data.page.account;
  const duration = data.venue.menu.labels;
  return {
    manage: booking['manage'],
    close: booking['close'],
    cancel: booking['cancel'],
    reschedule: booking['reschedule'],
    more: booking['more'],
    openMapText: booking['open-map-text'],
    prepaid: booking['prepaid'],
    payAtVenue: booking['pay-at-venue'],
    withStylist: booking['with-stylist'],
    anyStylist: booking['any-stylist'],
    spaBreakNights: booking['spa-break-nights'],
    spaDay: booking['spa-day'],
    orderReference: booking['order-reference'],
    durationOptions: {
      hour: duration.hour,
      hours: duration.hours,
      minutes: duration.minutes,
    },
  };
};

export const selectCmsAddToCalendarLabels = (
  state: AppRootState
): AddToCalendarLabels => {
  const data = selectCmsData(state);
  const {'add-to-calendar': addToCalendar} = data.page.account;
  return {
    bodyTemplate: addToCalendar['body-template'],
    btn: addToCalendar.btn,
    titleTemplate: addToCalendar['title-template'],
  };
};

export const selectCmsPreviousOrderHeaderLabel = (
  state: AppRootState
): string => {
  const data = selectCmsData(state);
  const {account} = data.page;
  return account['bookings-list']['previous-text'];
};

export const selectCmsUpcomingOrderHeaderLabel = (
  state: AppRootState
): string => {
  const data = selectCmsData(state);
  const {account} = data.page;
  return account['bookings-list']['upcoming-text'];
};

export const selectCmsServicePopupLabels = (
  state: AppRootState
): ServicePopupLabels => {
  const data = selectCmsData(state);
  const {
    common,
    page: {venue: venuePage},
  } = data;
  return {
    closeText: common['popup']['close-button'],
    popupAboutText: venuePage['service-details'].about.header,
    popupHeaderText: venuePage['service-details'].title,
    popupRestrictionsText: venuePage['service-details'].restrictions.header,
    popupInfoText: venuePage['service-details']['good-to-know'].header,
  };
};

export const selectCmsOrdersPageTitle = (
  state: AppRootState
): {
  titleTemplate: string;
  descriptionTemplate: string;
} => {
  const data = selectCmsData(state);
  const {account} = data.page;
  return {
    titleTemplate: account.head['title-template'],
    descriptionTemplate: account.head['description-template'],
  };
};

export const selectCmsOrdersNoneLabels = (
  state: AppRootState
): NoOrdersLabels => {
  const data = selectCmsData(state);
  const {account} = data.page;
  return {
    title: account['bookings-empty'].title,
    content: account['bookings-empty'].content,
    button: account['bookings-empty'].button,
    switchLinkText: account['bookings-empty']['account-switch-link-text'],
    switchLinkTitle: account['bookings-empty']['account-switch-title'],
    emailLabel: account['bookings-empty']['email-label'],
  };
};

export const selectCmsNoUpcomingOrderLabels = (
  state: AppRootState
): NoUpcomingOrdersLabels => {
  const data = selectCmsData(state);
  const {account} = data.page;
  return {
    title: account['bookings-empty'].title,
    body: account['bookings-empty'].content,
    cta: account['bookings-empty'].button,
  };
};

export const selectCmsErrorMessages = (state: AppRootState): ErrorMessages => {
  const data = selectCmsRootState(state).data as CmsData;
  const {
    page: {error},
  } = data;

  return {
    404: {
      description: error['404'].description,
    },
    generic: {
      header: error.generic.header,
      description: error.generic.description,
    },
  };
};

export const selectGtmConfig = (state: AppRootState): GtmConfig => {
  const data = selectCmsRootState(state).data as CmsData;
  return data.analytics['google-tag-manager'];
};

export const selectCmsPageWallet = (state: AppRootState): CmsPageWallet => {
  const data = selectCmsRootState(state).data as CmsData;
  return data.page.wallet;
};

export const selectDiscountFormLabels = (
  state: AppRootState
): WalletFormCms => {
  const data = selectCmsRootState(state).data as CmsData;
  return data.page.wallet.form;
};
